import React, { useState, useEffect, useCallback, useMemo } from "react";
import { inventoryApi, categoryApi } from "../services/api";
import {
  FiBox,
  FiSearch,
  FiLayers,
  FiAlertTriangle,
  FiRefreshCw,
  FiX,
  FiChevronLeft,
  FiChevronRight,
  FiCheck,
  FiMonitor,
  FiCpu,
  FiHardDrive,
  FiServer,
  FiDisc,
  FiShoppingBag,
  FiFolder,
  FiTag,
  FiDollarSign,
  FiChevronDown,
  FiChevronUp,
} from "react-icons/fi";
import { toast } from "react-toastify";

// Helper icon picker based on category name
const getCategoryIcon = (categoryName) => {
  const name = String(categoryName || "").toLowerCase();
  if (name.includes("desktop") || name.includes("cpu") || name.includes("server")) return FiServer;
  if (name.includes("led") || name.includes("monitor") || name.includes("display")) return FiMonitor;
  if (name.includes("nvme") || name.includes("ssd") || name.includes("storage") || name.includes("hard")) return FiHardDrive;
  if (name.includes("ram") || name.includes("memory")) return FiCpu;
  if (name.includes("license") || name.includes("windows") || name.includes("office") || name.includes("software")) return FiDisc;
  if (name.includes("bag") || name.includes("backpack")) return FiShoppingBag;
  if (name.includes("mouse") || name.includes("pad") || name.includes("keyboard")) return FiLayers;
  return FiBox;
};

const StockPage = () => {
  const [items, setItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [loading, setLoading] = useState(true);

  // Filter States: Category & Subcategory Drill-down
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [selectedSubcategoryId, setSelectedSubcategoryId] = useState("");
  const [search, setSearch] = useState("");

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [itemsRes, catsRes, subsRes] = await Promise.all([
        inventoryApi.getAll({ is_sale: 0 }),
        categoryApi.getCategories(),
        categoryApi.getSubcategories(),
      ]);

      const allItems = itemsRes.data || [];
      // Only keep items where is_sale = 0 (unsold items currently in stock)
      const unsoldStockItems = allItems.filter(
        (item) => !item.is_sale && item.is_sale !== 1 && item.is_sale !== "1" && item.is_sale !== true
      );

      setItems(unsoldStockItems);
      setCategories(catsRes.data || []);
      setSubcategories(subsRes.data || []);
    } catch (err) {
      toast.error(err.message || "Failed to load stock data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Overall Inventory Stats
  const totalItemsCount = useMemo(() => {
    return items.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 1), 0);
  }, [items]);

  const totalStockValuation = useMemo(() => {
    return items.reduce((sum, item) => {
      const q = parseInt(item.quantity, 10) || 1;
      const p = parseFloat(item.price) || 0;
      return sum + q * p;
    }, 0);
  }, [items]);

  // === 1. CATEGORY CARDS CALCULATION (Parent Level) ===
  const categoryCards = useMemo(() => {
    const list = categories.map((cat) => {
      // Items belonging to this category
      const cItems = items.filter((it) => String(it.category_id) === String(cat.id));
      const totalUnits = cItems.reduce((sum, it) => sum + (parseInt(it.quantity, 10) || 1), 0);

      // Subcategories belonging to this category
      const cSubs = subcategories.filter((s) => String(s.category_id) === String(cat.id));

      return {
        id: String(cat.id),
        name: cat.name,
        totalUnits,
        distinctItemCount: cItems.length,
        subcategoriesCount: cSubs.length,
        isLowStock: totalUnits > 0 && totalUnits < 5,
        isOutOfStock: totalUnits === 0,
      };
    });

    // Also include any items that might have a category not found in categories table
    items.forEach((it) => {
      if (it.category_id && !list.some((c) => c.id === String(it.category_id))) {
        const cItems = items.filter((i) => String(i.category_id) === String(it.category_id));
        const totalUnits = cItems.reduce((sum, i) => sum + (parseInt(i.quantity, 10) || 1), 0);
        list.push({
          id: String(it.category_id),
          name: it.category_name || "General",
          totalUnits,
          distinctItemCount: cItems.length,
          subcategoriesCount: 1,
          isLowStock: totalUnits > 0 && totalUnits < 5,
          isOutOfStock: totalUnits === 0,
        });
      }
    });

    // Sort: categories with available stock first, then alphabetically
    list.sort((a, b) => b.totalUnits - a.totalUnits || a.name.localeCompare(b.name));
    return list;
  }, [categories, items, subcategories]);

  // Currently Selected Category Object
  const selectedCategory = useMemo(() => {
    if (!selectedCategoryId) return null;
    return categoryCards.find((c) => c.id === String(selectedCategoryId)) || null;
  }, [selectedCategoryId, categoryCards]);

  // === 2. SUBCATEGORY CARDS CALCULATION (Child Level for Selected Category) ===
  const subcategoryCardsForSelectedCategory = useMemo(() => {
    if (!selectedCategoryId) return [];

    // Filter subcategories belonging to this category
    const relevantSubs = subcategories.filter(
      (s) => String(s.category_id) === String(selectedCategoryId)
    );

    const list = relevantSubs.map((sub) => {
      const sItems = items.filter(
        (it) =>
          String(it.category_id) === String(selectedCategoryId) &&
          String(it.subcategory_id || it.sub_category_id) === String(sub.id)
      );
      const totalUnits = sItems.reduce((sum, it) => sum + (parseInt(it.quantity, 10) || 1), 0);

      return {
        id: String(sub.id),
        name: sub.name,
        category_id: sub.category_id,
        totalUnits,
        distinctItemCount: sItems.length,
        isLowStock: totalUnits > 0 && totalUnits < 5,
        isOutOfStock: totalUnits === 0,
      };
    });

    // Include subcategories present in items under this category if any missing
    items
      .filter((it) => String(it.category_id) === String(selectedCategoryId))
      .forEach((it) => {
        const subId = it.subcategory_id || it.sub_category_id;
        if (subId && !list.some((s) => s.id === String(subId))) {
          const sItems = items.filter(
            (i) =>
              String(i.category_id) === String(selectedCategoryId) &&
              String(i.subcategory_id || i.sub_category_id) === String(subId)
          );
          const totalUnits = sItems.reduce((sum, i) => sum + (parseInt(i.quantity, 10) || 1), 0);
          list.push({
            id: String(subId),
            name: it.subcategory_name || "Default",
            category_id: selectedCategoryId,
            totalUnits,
            distinctItemCount: sItems.length,
            isLowStock: totalUnits > 0 && totalUnits < 5,
            isOutOfStock: totalUnits === 0,
          });
        }
      });

    // Sort: subcategories with stock first, then alphabetically
    list.sort((a, b) => b.totalUnits - a.totalUnits || a.name.localeCompare(b.name));
    return list;
  }, [selectedCategoryId, subcategories, items]);

  // Currently Selected Subcategory Object
  const selectedSubcategory = useMemo(() => {
    if (!selectedSubcategoryId) return null;
    return (
      subcategoryCardsForSelectedCategory.find((s) => s.id === String(selectedSubcategoryId)) || null
    );
  }, [selectedSubcategoryId, subcategoryCardsForSelectedCategory]);

  // === 3. FILTERED STOCK ITEMS TABLE DATA ===
  const filteredItems = useMemo(() => {
    return items.filter((item) => {
      // 1. Category Filter
      if (selectedCategoryId && String(item.category_id) !== String(selectedCategoryId)) {
        return false;
      }

      // 2. Subcategory Filter
      if (
        selectedSubcategoryId &&
        String(item.subcategory_id || item.sub_category_id) !== String(selectedSubcategoryId)
      ) {
        return false;
      }

      // 3. Search Filter
      if (search.trim()) {
        const q = search.toLowerCase().trim();
        const matchesName = item.item_name?.toLowerCase().includes(q);
        const matchesSku = item.sku?.toLowerCase().includes(q);
        const matchesSn = item.serial_number?.toLowerCase().includes(q);
        const matchesPn = item.part_number?.toLowerCase().includes(q);
        const matchesCat = item.category_name?.toLowerCase().includes(q);
        const matchesSub = item.subcategory_name?.toLowerCase().includes(q);
        if (!matchesName && !matchesSku && !matchesSn && !matchesPn && !matchesCat && !matchesSub) {
          return false;
        }
      }

      return true;
    });
  }, [items, selectedCategoryId, selectedSubcategoryId, search]);

  // Reset page when any filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [selectedCategoryId, selectedSubcategoryId, search, pageSize]);

  // Pagination Calculations
  const totalPages = Math.max(1, Math.ceil(filteredItems.length / pageSize));
  const startIndex = (currentPage - 1) * pageSize;
  const endIndex = Math.min(startIndex + pageSize, filteredItems.length);
  const paginatedItems = useMemo(() => {
    return filteredItems.slice(startIndex, endIndex);
  }, [filteredItems, startIndex, endIndex]);

  // Total Units in Current Filtered View
  const filteredUnitsCount = useMemo(() => {
    return filteredItems.reduce((sum, item) => sum + (parseInt(item.quantity, 10) || 1), 0);
  }, [filteredItems]);

  const filteredValuation = useMemo(() => {
    return filteredItems.reduce((sum, item) => {
      const q = parseInt(item.quantity, 10) || 1;
      const p = parseFloat(item.price) || 0;
      return sum + q * p;
    }, 0);
  }, [filteredItems]);

  // Category Selection Handler (Toggles or selects)
  const handleCategoryClick = (catId) => {
    if (String(selectedCategoryId) === String(catId)) {
      // Clicking same category again resets to all
      setSelectedCategoryId("");
      setSelectedSubcategoryId("");
    } else {
      setSelectedCategoryId(String(catId));
      setSelectedSubcategoryId(""); // Reset subcategory when category changes
    }
  };

  // Subcategory Selection Handler
  const handleSubcategoryClick = (subId) => {
    if (String(selectedSubcategoryId) === String(subId)) {
      setSelectedSubcategoryId(""); // Reset to all in this category
    } else {
      setSelectedSubcategoryId(String(subId));
    }
  };

  const handleResetAllFilters = () => {
    setSelectedCategoryId("");
    setSelectedSubcategoryId("");
    setSearch("");
  };

  return (
    <div className="fade-in" style={{ paddingBottom: 40 }}>
      {/* Page Header */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 16,
          marginBottom: 20,
        }}
      >
        <div>
          <h2 style={{ fontSize: "1.45rem", fontWeight: 800, color: "var(--navy)", margin: 0 }}>
            Available Stock{" "}
            <span
              style={{
                fontSize: "0.82rem",
                backgroundColor: "var(--primary-light, #ECFDF5)",
                color: "var(--primary, #064E3B)",
                padding: "3px 10px",
                borderRadius: 20,
                fontWeight: 700,
                marginLeft: 6,
              }}
            >
              Live Inventory
            </span>
          </h2>
          <p style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginTop: 4, marginBottom: 0 }}>
            Real-time on-hand inventory. Click any <strong>Category</strong> card below to explore its <strong>Subcategories</strong> and item counts.
          </p>
        </div>

        <button
          onClick={fetchData}
          title="Refresh Stock Data"
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            padding: "9px 16px",
            borderRadius: "var(--radius-md)",
            border: "1px solid var(--slate-300)",
            backgroundColor: "#fff",
            color: "var(--slate-700)",
            fontSize: "0.85rem",
            cursor: "pointer",
            fontWeight: 700,
            boxShadow: "var(--shadow-sm)",
            transition: "all 0.15s ease",
          }}
        >
          <FiRefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* ========================================================
          STEP 1: CATEGORY CARDS GRID (Parent Level)
         ======================================================== */}
      <div style={{ marginBottom: 22 }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 12,
            flexWrap: "wrap",
            gap: 8,
          }}
        >
          <div
            style={{
              fontSize: "0.95rem",
              fontWeight: 800,
              color: "var(--navy)",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <FiFolder size={18} style={{ color: "var(--primary)" }} />
            <span>Categories Overview</span>
            <span
              style={{
                fontSize: "0.75rem",
                color: "var(--slate-500)",
                backgroundColor: "var(--slate-100)",
                padding: "2px 8px",
                borderRadius: 12,
                fontWeight: 600,
              }}
            >
              {categoryCards.length} Categories
            </span>
          </div>

          {selectedCategoryId && (
            <button
              onClick={() => {
                setSelectedCategoryId("");
                setSelectedSubcategoryId("");
              }}
              style={{
                background: "none",
                border: "none",
                color: "var(--primary)",
                fontWeight: 700,
                fontSize: "0.82rem",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 5,
              }}
            >
              <FiX size={14} /> View All Categories
            </button>
          )}
        </div>

        {/* Categories Cards Grid */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
            gap: 12,
            padding: "2px 2px 6px 2px",
          }}
        >
          {/* Card 0: "All Categories" Summary Card */}
          <div
            onClick={() => {
              setSelectedCategoryId("");
              setSelectedSubcategoryId("");
            }}
            style={{
              backgroundColor: !selectedCategoryId ? "var(--primary-light, #ECFDF5)" : "#fff",
              border: !selectedCategoryId
                ? "2px solid var(--primary, #064E3B)"
                : "1px solid var(--slate-200)",
              borderRadius: "var(--radius-lg, 12px)",
              padding: "16px 18px",
              cursor: "pointer",
              boxShadow: !selectedCategoryId ? "0 4px 14px rgba(6, 78, 59, 0.12)" : "var(--shadow-sm)",
              transition: "all 0.15s ease",
              position: "relative",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              minHeight: 120,
            }}
            onMouseEnter={(e) => {
              if (selectedCategoryId) {
                e.currentTarget.style.borderColor = "var(--primary)";
                e.currentTarget.style.transform = "translateY(-2px)";
              }
            }}
            onMouseLeave={(e) => {
              if (selectedCategoryId) {
                e.currentTarget.style.borderColor = "var(--slate-200)";
                e.currentTarget.style.transform = "translateY(0)";
              }
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
              <div
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  backgroundColor: !selectedCategoryId ? "var(--primary)" : "var(--slate-100)",
                  color: !selectedCategoryId ? "#fff" : "var(--slate-600)",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <FiBox size={18} />
              </div>

              {!selectedCategoryId && (
                <span
                  style={{
                    fontSize: "0.7rem",
                    fontWeight: 800,
                    backgroundColor: "var(--primary)",
                    color: "#fff",
                    padding: "2px 8px",
                    borderRadius: 10,
                    display: "flex",
                    alignItems: "center",
                    gap: 3,
                  }}
                >
                  <FiCheck size={11} /> All Stock
                </span>
              )}
            </div>

            <div>
              <div style={{ fontSize: "0.96rem", fontWeight: 800, color: "var(--navy)" }}>All Categories</div>
              <div style={{ fontSize: "0.75rem", color: "var(--slate-400)", marginTop: 2 }}>
                Complete Live Catalog
              </div>
            </div>

            <div style={{ marginTop: 12, display: "flex", alignItems: "baseline", gap: 6 }}>
              <span style={{ fontSize: "1.6rem", fontWeight: 900, color: "var(--navy)" }}>
                {totalItemsCount}
              </span>
              <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--slate-500)" }}>
                Total Units
              </span>
            </div>
          </div>

          {/* Individual Category Cards */}
          {categoryCards.map((cat) => {
            const isSelected = String(selectedCategoryId) === String(cat.id);
            const IconComponent = getCategoryIcon(cat.name);

            return (
              <div
                key={cat.id}
                onClick={() => handleCategoryClick(cat.id)}
                style={{
                  backgroundColor: isSelected ? "var(--primary-light, #ECFDF5)" : "#fff",
                  border: isSelected
                    ? "2px solid var(--primary, #064E3B)"
                    : cat.isOutOfStock
                    ? "1px dashed var(--slate-300)"
                    : cat.isLowStock
                    ? "1px solid #FCD34D"
                    : "1px solid var(--slate-200)",
                  borderRadius: "var(--radius-lg, 12px)",
                  padding: "16px 18px",
                  cursor: "pointer",
                  boxShadow: isSelected ? "0 4px 14px rgba(6, 78, 59, 0.14)" : "var(--shadow-sm)",
                  transition: "all 0.15s ease",
                  position: "relative",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  minHeight: 120,
                  opacity: cat.isOutOfStock && !isSelected ? 0.7 : 1,
                }}
                onMouseEnter={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = "var(--primary)";
                    e.currentTarget.style.transform = "translateY(-2px)";
                    e.currentTarget.style.boxShadow = "0 6px 16px rgba(0,0,0,0.06)";
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isSelected) {
                    e.currentTarget.style.borderColor = cat.isOutOfStock
                      ? "var(--slate-300)"
                      : cat.isLowStock
                      ? "#FCD34D"
                      : "var(--slate-200)";
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow = "var(--shadow-sm)";
                  }
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 8 }}>
                  <div
                    style={{
                      width: 34,
                      height: 34,
                      borderRadius: 8,
                      backgroundColor: isSelected ? "var(--primary)" : "var(--slate-100)",
                      color: isSelected ? "#fff" : "var(--primary)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <IconComponent size={18} />
                  </div>

                  <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", justifyContent: "flex-end" }}>
                    {cat.isOutOfStock && (
                      <span
                        style={{
                          fontSize: "0.68rem",
                          fontWeight: 800,
                          backgroundColor: "#F1F5F9",
                          color: "var(--slate-500)",
                          padding: "2px 7px",
                          borderRadius: 10,
                        }}
                      >
                        Out of Stock
                      </span>
                    )}

                    {cat.isLowStock && (
                      <span
                        style={{
                          fontSize: "0.68rem",
                          fontWeight: 800,
                          backgroundColor: "#FEF3C7",
                          color: "#B45309",
                          padding: "2px 7px",
                          borderRadius: 10,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 3,
                          border: "1px solid #FDE68A",
                        }}
                      >
                        <FiAlertTriangle size={10} /> Low
                      </span>
                    )}

                    {isSelected && (
                      <span
                        style={{
                          fontSize: "0.7rem",
                          fontWeight: 800,
                          backgroundColor: "var(--primary)",
                          color: "#fff",
                          padding: "2px 8px",
                          borderRadius: 10,
                          display: "flex",
                          alignItems: "center",
                          gap: 3,
                        }}
                      >
                        <FiCheck size={11} /> Selected
                      </span>
                    )}
                  </div>
                </div>

                <div>
                  <div
                    style={{
                      fontSize: "0.96rem",
                      fontWeight: 800,
                      color: isSelected ? "var(--primary, #064E3B)" : "var(--navy)",
                      lineHeight: 1.25,
                    }}
                  >
                    {cat.name}
                  </div>
                  <div style={{ fontSize: "0.74rem", color: "var(--slate-500)", marginTop: 2 }}>
                    {cat.subcategoriesCount}{" "}
                    {cat.subcategoriesCount === 1 ? "Subcategory" : "Subcategories"}
                  </div>
                </div>

                {/* Units Count */}
                <div style={{ marginTop: 12, display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
                  <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
                    <span
                      style={{
                        fontSize: "1.55rem",
                        fontWeight: 900,
                        color: cat.isOutOfStock
                          ? "var(--slate-400)"
                          : cat.isLowStock
                          ? "#B45309"
                          : "var(--navy)",
                      }}
                    >
                      {cat.totalUnits}
                    </span>
                    <span style={{ fontSize: "0.78rem", fontWeight: 700, color: "var(--slate-500)" }}>
                      Units
                    </span>
                  </div>

                  <span
                    style={{
                      fontSize: "0.72rem",
                      fontWeight: 700,
                      color: isSelected ? "var(--primary)" : "var(--slate-400)",
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                    }}
                  >
                    {isSelected ? "Hide" : "Expand"}
                    {isSelected ? <FiChevronUp size={12} /> : <FiChevronDown size={12} />}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ========================================================
          STEP 2: SUBCATEGORIES SECTION (Reveals on Category Click)
         ======================================================== */}
      {selectedCategory && (
        <div
          style={{
            backgroundColor: "#F8FAFC",
            border: "1.5px solid var(--primary-light, #BBF7D0)",
            borderRadius: "var(--radius-lg, 12px)",
            padding: "18px 20px",
            marginBottom: 22,
            boxShadow: "0 2px 8px rgba(6, 78, 59, 0.06)",
          }}
        >
          {/* Subcategory Section Header */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: 14,
              flexWrap: "wrap",
              gap: 10,
            }}
          >
            <div>
              <div
                style={{
                  fontSize: "1.02rem",
                  fontWeight: 800,
                  color: "var(--navy)",
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                }}
              >
                <FiLayers size={18} style={{ color: "var(--primary)" }} />
                <span>
                  {selectedCategory.name} &bull; Subcategories & Counts
                </span>
                <span
                  style={{
                    fontSize: "0.75rem",
                    backgroundColor: "var(--primary)",
                    color: "#fff",
                    padding: "2px 8px",
                    borderRadius: 12,
                    fontWeight: 700,
                  }}
                >
                  {selectedCategory.totalUnits} Total Units
                </span>
              </div>
              <p style={{ fontSize: "0.8rem", color: "var(--slate-500)", margin: "4px 0 0 0" }}>
                Click any subcategory below to filter items, or click <strong>All {selectedCategory.name}</strong> to view all.
              </p>
            </div>

            <button
              onClick={() => setSelectedSubcategoryId("")}
              style={{
                padding: "6px 14px",
                borderRadius: 8,
                border: "1px solid var(--slate-300)",
                backgroundColor: !selectedSubcategoryId ? "var(--primary)" : "#fff",
                color: !selectedSubcategoryId ? "#fff" : "var(--navy)",
                fontSize: "0.8rem",
                fontWeight: 700,
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
            >
              All {selectedCategory.name} ({selectedCategory.totalUnits} Units)
            </button>
          </div>

          {/* Subcategories Cards / Chips */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(200px, 1fr))",
              gap: 10,
            }}
          >
            {subcategoryCardsForSelectedCategory.length === 0 ? (
              <div style={{ padding: "12px", color: "var(--slate-400)", fontSize: "0.85rem" }}>
                No subcategories configured for this category yet.
              </div>
            ) : (
              subcategoryCardsForSelectedCategory.map((sub) => {
                const isSubSelected = String(selectedSubcategoryId) === String(sub.id);

                return (
                  <div
                    key={sub.id}
                    onClick={() => handleSubcategoryClick(sub.id)}
                    style={{
                      backgroundColor: isSubSelected ? "#ECFDF5" : "#fff",
                      border: isSubSelected
                        ? "2px solid var(--primary, #064E3B)"
                        : sub.isOutOfStock
                        ? "1px dashed var(--slate-300)"
                        : sub.isLowStock
                        ? "1px solid #FCD34D"
                        : "1px solid var(--slate-200)",
                      borderRadius: 10,
                      padding: "12px 14px",
                      cursor: "pointer",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "space-between",
                      boxShadow: isSubSelected ? "0 2px 8px rgba(6, 78, 59, 0.12)" : "0 1px 3px rgba(0,0,0,0.02)",
                      transition: "all 0.15s ease",
                    }}
                    onMouseEnter={(e) => {
                      if (!isSubSelected) {
                        e.currentTarget.style.borderColor = "var(--primary)";
                        e.currentTarget.style.transform = "translateY(-1px)";
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (!isSubSelected) {
                        e.currentTarget.style.borderColor = sub.isOutOfStock
                          ? "var(--slate-300)"
                          : sub.isLowStock
                          ? "#FCD34D"
                          : "var(--slate-200)";
                        e.currentTarget.style.transform = "translateY(0)";
                      }
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
                      <span
                        style={{
                          fontSize: "0.88rem",
                          fontWeight: 800,
                          color: isSubSelected ? "var(--primary, #064E3B)" : "var(--navy)",
                        }}
                      >
                        {sub.name}
                      </span>

                      {isSubSelected && (
                        <span
                          style={{
                            fontSize: "0.68rem",
                            backgroundColor: "var(--primary)",
                            color: "#fff",
                            padding: "1px 6px",
                            borderRadius: 6,
                            fontWeight: 800,
                          }}
                        >
                          ✓
                        </span>
                      )}
                    </div>

                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", marginTop: 4 }}>
                      <div style={{ display: "flex", alignItems: "baseline", gap: 4 }}>
                        <span
                          style={{
                            fontSize: "1.3rem",
                            fontWeight: 900,
                            color: sub.isOutOfStock
                              ? "var(--slate-400)"
                              : sub.isLowStock
                              ? "#B45309"
                              : "var(--navy)",
                          }}
                        >
                          {sub.totalUnits}
                        </span>
                        <span style={{ fontSize: "0.72rem", color: "var(--slate-500)", fontWeight: 600 }}>
                          Units
                        </span>
                      </div>

                      {sub.isOutOfStock ? (
                        <span style={{ fontSize: "0.68rem", color: "var(--slate-400)", fontWeight: 600 }}>
                          0 in stock
                        </span>
                      ) : sub.isLowStock ? (
                        <span style={{ fontSize: "0.68rem", color: "#B45309", fontWeight: 700 }}>
                          Low Stock
                        </span>
                      ) : null}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </div>
      )}

      {/* ========================================================
          STEP 3: SEARCH & QUICK FILTER BAR
         ======================================================== */}
      <div
        className="no-print"
        style={{
          backgroundColor: "#fff",
          borderRadius: "var(--radius-lg)",
          padding: "14px 18px",
          border: "1px solid var(--slate-200)",
          boxShadow: "var(--shadow-sm)",
          marginBottom: 16,
          display: "flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        {/* Search input */}
        <div style={{ position: "relative", flex: 1, minWidth: 260 }}>
          <FiSearch
            size={17}
            style={{
              position: "absolute",
              left: 12,
              top: "50%",
              transform: "translateY(-50%)",
              color: "var(--slate-400)",
            }}
          />
          <input
            type="text"
            placeholder="Search stock by item name, SKU, serial #, category, subcategory..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{
              width: "100%",
              padding: "9px 34px 9px 36px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--slate-300)",
              fontSize: "0.88rem",
              outline: "none",
              boxSizing: "border-box",
            }}
          />
          {search && (
            <button
              onClick={() => setSearch("")}
              style={{
                position: "absolute",
                right: 10,
                top: "50%",
                transform: "translateY(-50%)",
                background: "none",
                border: "none",
                cursor: "pointer",
                color: "var(--slate-400)",
                display: "flex",
                alignItems: "center",
              }}
            >
              <FiX size={15} />
            </button>
          )}
        </div>

        {/* Active Filter Badges */}
        {(selectedCategoryId || selectedSubcategoryId || search) && (
          <button
            onClick={handleResetAllFilters}
            style={{
              padding: "9px 16px",
              borderRadius: "var(--radius-md)",
              border: "1px solid var(--slate-200)",
              backgroundColor: "var(--slate-100)",
              color: "var(--slate-700)",
              fontSize: "0.84rem",
              fontWeight: 700,
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <FiX size={14} /> Clear All Filters
          </button>
        )}
      </div>

      {/* Active Filter Breadcrumb Strip */}
      {(selectedCategory || selectedSubcategory || search) && (
        <div
          style={{
            backgroundColor: "var(--primary-light, #ECFDF5)",
            border: "1px solid rgba(6, 78, 59, 0.2)",
            borderRadius: "var(--radius-md)",
            padding: "10px 16px",
            marginBottom: 16,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: 10,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: "0.86rem", color: "var(--navy)", flexWrap: "wrap" }}>
            <span style={{ fontWeight: 700, color: "var(--slate-500)" }}>Filtered View:</span>

            {selectedCategory && (
              <span
                style={{
                  backgroundColor: "#fff",
                  padding: "3px 10px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  fontWeight: 800,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                Category: <strong style={{ color: "var(--primary)" }}>{selectedCategory.name}</strong>
                <FiX
                  size={12}
                  style={{ cursor: "pointer", color: "var(--slate-400)" }}
                  onClick={() => {
                    setSelectedCategoryId("");
                    setSelectedSubcategoryId("");
                  }}
                />
              </span>
            )}

            {selectedSubcategory && (
              <span
                style={{
                  backgroundColor: "#fff",
                  padding: "3px 10px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  fontWeight: 800,
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 5,
                }}
              >
                Subcategory: <strong style={{ color: "#4338CA" }}>{selectedSubcategory.name}</strong>
                <FiX
                  size={12}
                  style={{ cursor: "pointer", color: "var(--slate-400)" }}
                  onClick={() => setSelectedSubcategoryId("")}
                />
              </span>
            )}

            {search && (
              <span
                style={{
                  backgroundColor: "#fff",
                  padding: "3px 10px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  fontWeight: 700,
                }}
              >
                Query: "<em>{search}</em>"
              </span>
            )}

            <span
              style={{
                backgroundColor: "var(--primary)",
                color: "#fff",
                fontSize: "0.75rem",
                padding: "2px 8px",
                borderRadius: 12,
                fontWeight: 800,
              }}
            >
              {filteredUnitsCount} Units ({filteredItems.length} Records)
            </span>
          </div>

          <button
            onClick={handleResetAllFilters}
            style={{
              background: "none",
              border: "none",
              color: "var(--primary)",
              fontWeight: 800,
              fontSize: "0.82rem",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            Show Complete Catalog
          </button>
        </div>
      )}

      {/* ========================================================
          STEP 4: STOCK ITEMS DETAIL TABLE
         ======================================================== */}
      <div
        style={{
          backgroundColor: "#fff",
          borderRadius: "var(--radius-lg)",
          border: "1px solid var(--slate-200)",
          boxShadow: "var(--shadow-sm)",
          overflow: "hidden",
        }}
      >
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.86rem" }}>
            <thead>
              <tr
                style={{
                  backgroundColor: "var(--slate-50)",
                  borderBottom: "1.5px solid var(--slate-300)",
                  textAlign: "left",
                }}
              >
                <th style={{ padding: "12px 16px", color: "var(--slate-600)", fontWeight: 700, width: 36 }}>#</th>
                <th style={{ padding: "12px 16px", color: "var(--slate-700)", fontWeight: 700 }}>Item Description</th>
                <th style={{ padding: "12px 16px", color: "var(--slate-700)", fontWeight: 700 }}>Category</th>
                <th style={{ padding: "12px 16px", color: "var(--slate-700)", fontWeight: 700 }}>Sub-category</th>
                <th style={{ padding: "12px 16px", color: "var(--slate-700)", fontWeight: 700, textAlign: "center" }}>
                  In Stock / Qty
                </th>
                <th style={{ padding: "12px 16px", color: "#B45309", fontWeight: 700, textAlign: "center" }}>
                  Bottom Line
                </th>
                <th style={{ padding: "12px 16px", color: "var(--slate-700)", fontWeight: 700, textAlign: "right" }}>
                  Unit Cost (Rs.)
                </th>
                <th style={{ padding: "12px 16px", color: "var(--slate-700)", fontWeight: 700, textAlign: "right" }}>
                  Total Value (Rs.)
                </th>
                <th style={{ padding: "12px 16px", color: "var(--slate-700)", fontWeight: 700 }}>SKU Code</th>
                <th style={{ padding: "12px 16px", color: "var(--slate-700)", fontWeight: 700 }}>Serial Number</th>
                <th style={{ padding: "12px 16px", color: "var(--slate-700)", fontWeight: 700 }}>Warranty Status</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={11} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                    Loading available stock inventory...
                  </td>
                </tr>
              ) : filteredItems.length === 0 ? (
                <tr>
                  <td colSpan={11} style={{ padding: 40, textAlign: "center", color: "var(--slate-400)" }}>
                    <div style={{ fontWeight: 800, color: "var(--navy)", marginBottom: 4, fontSize: "1rem" }}>
                      No stock records found
                    </div>
                    <div style={{ fontSize: "0.82rem" }}>
                      No available stock matching the selected category, subcategory, or search query.
                    </div>
                  </td>
                </tr>
              ) : (
                paginatedItems.map((item, idx) => {
                  const qty = parseInt(item.quantity, 10) || 1;
                  const unitCost = parseFloat(item.price) || 0;
                  const totalValue = qty * unitCost;
                  const isWarrantyExpired = item.warranty_end_date && new Date(item.warranty_end_date) < new Date();

                  return (
                    <tr
                      key={item.id}
                      style={{
                        borderBottom: "1px solid var(--slate-100)",
                        transition: "background 0.15s",
                        backgroundColor: idx % 2 === 0 ? "#fff" : "#FAFAFA",
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = "var(--slate-50)")}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = idx % 2 === 0 ? "#fff" : "#FAFAFA")}
                    >
                      <td style={{ padding: "12px 16px", color: "var(--slate-400)", fontWeight: 600 }}>
                        {startIndex + idx + 1}
                      </td>

                      {/* Item Name */}
                      <td style={{ padding: "12px 16px" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <div
                            style={{
                              width: 32,
                              height: 32,
                              borderRadius: 8,
                              backgroundColor: "var(--primary-light, #ECFDF5)",
                              color: "var(--primary, #064E3B)",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              flexShrink: 0,
                            }}
                          >
                            <FiBox size={16} />
                          </div>
                          <div>
                            <span style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.88rem" }}>
                              {item.item_name}
                            </span>
                            {item.part_number && (
                              <div style={{ fontSize: "0.72rem", color: "var(--slate-500)", fontFamily: "monospace" }}>
                                PN: {item.part_number}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* Category */}
                      <td style={{ padding: "12px 16px" }}>
                        <span
                          style={{
                            fontWeight: 700,
                            backgroundColor: "#F1F5F9",
                            color: "var(--navy)",
                            padding: "3px 8px",
                            borderRadius: 6,
                            fontSize: "0.78rem",
                            display: "inline-block",
                          }}
                        >
                          {item.category_name || "—"}
                        </span>
                      </td>

                      {/* Sub-category */}
                      <td style={{ padding: "12px 16px" }}>
                        <span style={{ color: "var(--slate-700)", fontWeight: 600, fontSize: "0.82rem" }}>
                          {item.subcategory_name || "—"}
                        </span>
                      </td>

                      {/* Qty / In Stock */}
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        {(() => {
                          const minLimit = item.min_stock_alert !== undefined && item.min_stock_alert !== null ? parseInt(item.min_stock_alert) : 5;
                          const isLow = qty <= minLimit;
                          return (
                            <span
                              style={{
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 4,
                                padding: "3px 10px",
                                borderRadius: 14,
                                backgroundColor: isLow ? "#FEF3C7" : "#DCFCE7",
                                color: isLow ? "#B45309" : "#15803D",
                                fontWeight: 900,
                                fontSize: "0.86rem",
                                border: isLow ? "1px solid #FCD34D" : "1px solid #BBF7D0",
                              }}
                            >
                              {qty} {qty === 1 ? "unit" : "units"}
                            </span>
                          );
                        })()}
                      </td>

                      {/* Bottom Line */}
                      <td style={{ padding: "12px 16px", textAlign: "center" }}>
                        {(() => {
                          const minLimit = item.min_stock_alert !== undefined && item.min_stock_alert !== null ? parseInt(item.min_stock_alert) : 5;
                          const isAtOrBelow = qty <= minLimit;
                          return (
                            <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                              <span
                                style={{
                                  display: "inline-flex",
                                  alignItems: "center",
                                  gap: 4,
                                  padding: "2px 8px",
                                  borderRadius: 6,
                                  backgroundColor: isAtOrBelow ? "#FFFBEB" : "var(--slate-100)",
                                  color: isAtOrBelow ? "#B45309" : "var(--slate-600)",
                                  fontWeight: 800,
                                  fontSize: "0.80rem",
                                  border: isAtOrBelow ? "1px solid #FCD34D" : "1px solid var(--slate-200)",
                                }}
                              >
                                {isAtOrBelow && <span>⚠️</span>}
                                {minLimit} units
                              </span>
                              {isAtOrBelow && (
                                <span style={{ fontSize: "0.62rem", fontWeight: 800, color: "#DC2626", textTransform: "uppercase" }}>
                                  Reached
                                </span>
                              )}
                            </div>
                          );
                        })()}
                      </td>

                      {/* Unit Cost */}
                      <td style={{ padding: "12px 16px", textAlign: "right" }}>
                        <span style={{ fontWeight: 700, color: "var(--slate-700)", fontSize: "0.85rem" }}>
                          Rs. {unitCost.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </td>

                      {/* Total Value */}
                      <td style={{ padding: "12px 16px", textAlign: "right" }}>
                        <span style={{ fontWeight: 800, color: "var(--navy)", fontSize: "0.88rem" }}>
                          Rs. {totalValue.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </span>
                      </td>

                      {/* SKU Code */}
                      <td style={{ padding: "12px 16px" }}>
                        {item.sku ? (
                          <span
                            style={{
                              fontFamily: "monospace",
                              fontWeight: 700,
                              padding: "2px 7px",
                              borderRadius: 4,
                              backgroundColor: "#EFF6FF",
                              color: "#1D4ED8",
                              fontSize: "0.78rem",
                              border: "1px solid #DBEAFE",
                            }}
                          >
                            {item.sku}
                          </span>
                        ) : (
                          <span style={{ color: "var(--slate-400)" }}>—</span>
                        )}
                      </td>

                      {/* Serial Number */}
                      <td style={{ padding: "12px 16px" }}>
                        {item.serial_number ? (
                          <code
                            style={{
                              fontFamily: "monospace",
                              fontSize: "0.8rem",
                              color: "var(--navy)",
                              fontWeight: 700,
                              backgroundColor: "var(--slate-100)",
                              padding: "2px 7px",
                              borderRadius: 4,
                            }}
                          >
                            {item.serial_number}
                          </code>
                        ) : (
                          <span style={{ color: "var(--slate-400)", fontSize: "0.76rem" }}>
                            — <span style={{ color: "var(--slate-400)" }}>(Bulk Item)</span>
                          </span>
                        )}
                      </td>

                      {/* Warranty Status */}
                      <td style={{ padding: "12px 16px" }}>
                        {item.warranty_end_date ? (
                          <div>
                            <div
                              style={{
                                fontWeight: 700,
                                color: isWarrantyExpired ? "var(--danger, #EF4444)" : "var(--navy)",
                                fontSize: "0.8rem",
                              }}
                            >
                              {new Date(item.warranty_end_date).toLocaleDateString()}
                            </div>
                            <span
                              style={{
                                fontSize: "0.68rem",
                                fontWeight: 800,
                                color: isWarrantyExpired ? "var(--danger, #EF4444)" : "var(--success, #10B981)",
                              }}
                            >
                              {isWarrantyExpired ? "Expired" : "Active"}
                            </span>
                          </div>
                        ) : (
                          <span style={{ color: "var(--slate-400)", fontSize: "0.76rem" }}>No Warranty</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination & Summary Bar */}
        {!loading && filteredItems.length > 0 && (
          <div
            style={{
              padding: "14px 18px",
              borderTop: "1px solid var(--slate-200)",
              backgroundColor: "var(--slate-50)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
              fontSize: "0.84rem",
              color: "var(--slate-600)",
            }}
          >
            <div>
              Showing <strong>{startIndex + 1}</strong> to <strong>{endIndex}</strong> of{" "}
              <strong>{filteredItems.length}</strong> items &bull; Total Units:{" "}
              <strong style={{ color: "var(--primary)" }}>{filteredUnitsCount}</strong> &bull; Total Stock Value:{" "}
              <strong style={{ color: "var(--navy)" }}>
                Rs. {filteredValuation.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </strong>
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              {/* Page Size Selector */}
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span>Rows:</span>
                <select
                  value={pageSize}
                  onChange={(e) => setPageSize(Number(e.target.value))}
                  style={{
                    padding: "4px 8px",
                    borderRadius: 6,
                    border: "1px solid var(--slate-300)",
                    fontSize: "0.82rem",
                    backgroundColor: "#fff",
                    fontWeight: 600,
                  }}
                >
                  <option value={10}>10</option>
                  <option value={25}>25</option>
                  <option value={50}>50</option>
                </select>
              </div>

              {/* Prev Button */}
              <button
                disabled={currentPage === 1}
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "5px 12px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  backgroundColor: currentPage === 1 ? "var(--slate-100)" : "#fff",
                  color: currentPage === 1 ? "var(--slate-400)" : "var(--slate-700)",
                  cursor: currentPage === 1 ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                }}
              >
                <FiChevronLeft size={15} /> Prev
              </button>

              {/* Page Number Indicator */}
              <span style={{ fontWeight: 700, color: "var(--navy)" }}>
                Page {currentPage} of {totalPages}
              </span>

              {/* Next Button */}
              <button
                disabled={currentPage === totalPages}
                onClick={() => setCurrentPage((p) => Math.min(p + 1, totalPages))}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 4,
                  padding: "5px 12px",
                  borderRadius: 6,
                  border: "1px solid var(--slate-300)",
                  backgroundColor: currentPage === totalPages ? "var(--slate-100)" : "#fff",
                  color: currentPage === totalPages ? "var(--slate-400)" : "var(--slate-700)",
                  cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                  fontWeight: 600,
                  fontSize: "0.82rem",
                }}
              >
                Next <FiChevronRight size={15} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StockPage;
